import { memo, useMemo } from 'react';
import { Line, Text } from '@react-three/drei';
import type { Entity } from '../state/store';
import { useAppStore } from '../state/store';
import { useBlockStore } from '../state/commandAuxStores';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { SolidEntityMesh } from './SolidEntityMesh';

/**
 * Renders a block-reference ('insert') entity: the definition's components
 * translated to the insertion point. Selection/lock run through the ledger
 * for the reference itself (the components are display-only children).
 */
export const BlockRefMesh = memo(function BlockRefMesh({
    entity,
    isSelected,
    isHovered,
}: {
    entity: Entity;
    isSelected: boolean;
    isHovered: boolean;
}) {
    const defs = useBlockStore((s) => s.defs);
    const blockName = String(entity.data.blockName ?? '');
    const def = defs[blockName];
    const pos = (entity.data.position as number[]) ?? [0, 0, 0];
    const scale = typeof entity.data.scale === 'number' && entity.data.scale > 0 ? entity.data.scale : 1;
    const rotDeg = typeof entity.data.rotation === 'number' ? entity.data.rotation : 0;
    const rotRad = (rotDeg * Math.PI) / 180;
    const attrValues = (entity.data.attributeValues ?? {}) as Record<string, string>;
    // Dynamic-block visibility states: when the instance has an active
    // state, definition children tagged with a DIFFERENT state hide.
    const activeState = entity.data.visibilityState as string | undefined;
    // XCLIP: world-space clip polygon. Children anchor points outside it
    // hide. The boundary is inverse-transformed into definition-local space
    // once per render so the (translated) child points can be tested cheaply.
    const clipBoundary = entity.data.clipBoundary as number[][] | undefined;

    const refCallback = useEntityRegistryRef(entity.id, entity.layer);

    const handleClick = (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        dispatchEntityClick(entity.id, e as never);
    };

    // BYBLOCK resolution: components without an explicit color draw in the
    // theme ink (block definitions themselves are usually uncolored).
    const ink = useAnnotationInk();
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || ink;

    // Full instance transform lives on the wrapper group (position + Z
    // rotation + uniform scale), so children render in definition-local
    // coordinates. Attribute values replace <TAG> text children.
    const children = useMemo(() => {
        if (!def) return null;
        // World-space position of a definition-local anchor (same transform
        // the wrapper group applies): needed for the XCLIP boundary test.
        const toWorld = (p: number[] | undefined): [number, number] | null => {
            if (!Array.isArray(p)) return null;
            const x = ((p[0] ?? 0) * scale), y = ((p[1] ?? 0) * scale);
            return [pos[0] + x * Math.cos(rotRad) - y * Math.sin(rotRad),
                pos[1] + x * Math.sin(rotRad) + y * Math.cos(rotRad)];
        };
        const clipOk = (anchor: number[] | undefined): boolean => {
            if (!clipBoundary || clipBoundary.length < 3) return true;
            const w = toWorld(anchor);
            if (!w) return true;
            // Even-odd point-in-polygon (world XY).
            let inside = false;
            for (let i = 0, j = clipBoundary.length - 1; i < clipBoundary.length; j = i++) {
                const xi = clipBoundary[i][0], yi = clipBoundary[i][1];
                const xj = clipBoundary[j][0], yj = clipBoundary[j][1];
                if (((yi > w[1]) !== (yj > w[1]))
                    && (w[0] < ((xj - xi) * (w[1] - yi)) / (yj - yi + 1e-12) + xi)) inside = !inside;
            }
            return inside;
        };
        return def.entities.map((src, i) => {
            const d = src.data as Record<string, any>;
            if (activeState && typeof d.visState === 'string' && d.visState !== activeState) return null;
            if (!clipOk(d.position ?? d.start ?? d.center ?? d.vertices?.[0])) return null;
            switch (src.type) {
                case 'line':
                    return <Line key={i} points={[d.start, d.end]} color={color} lineWidth={1.5} />;
                case 'polyline':
                case 'polygon':
                    return <Line key={i} points={d.vertices as [number, number, number][]} color={color} lineWidth={1.5} />;
                case 'circle':
                    return <Line key={i} points={circlePts(d.center, d.radius)} color={color} lineWidth={1.5} />;
                case 'arc':
                    return <Line key={i} points={arcPts(d.center, d.radius, d.startAngle, d.endAngle)} color={color} lineWidth={1.5} />;
                case 'attdef': {
                    // Render the resolved attribute value (or the default),
                    // not the tag placeholder, when the instance carries it.
                    const tag = String(d.tag ?? '');
                    const shown = attrValues[tag] ?? String(d.defaultValue ?? `?${tag}?`);
                    return (
                        <Text key={i} position={[d.position?.[0] ?? 0, d.position?.[1] ?? 0, d.position?.[2] ?? 0]}
                            fontSize={0.25} color={color} rotation={[0, 0, d.rotation ?? 0]}>
                            {shown}
                        </Text>
                    );
                }
                case 'text':
                case 'mtext':
                    return (
                        <Text key={i} position={d.position} fontSize={0.25} color={color} rotation={[0, 0, d.rotation ?? 0]}>
                            {String(d.content ?? '')}
                        </Text>
                    );
                case 'solid':
                    return (
                        <group key={i} position={[d.position?.[0] ?? 0, d.position?.[1] ?? 0, d.position?.[2] ?? 0]}>
                            <SolidEntityMesh
                                entity={{ ...src, id: `${entity.id}-c${i}`, data: { ...d, position: [0, 0, 0] } }}
                                isSelected={false}
                                isHovered={false}
                            />
                        </group>
                    );
                default:
                    return null;
            }
        });
    }, [def, color, attrValues, activeState, clipBoundary, scale, rotRad, pos]);

    if (!def) {
        // Missing definition: draw a placeholder marker at the insertion point.
        return (
            <group ref={refCallback} onClick={handleClick} position={[pos[0], pos[1], pos[2]]}>
                <Line points={[[-0.3, -0.3, 0], [0.3, 0.3, 0]]} color="#ff5555" lineWidth={2} />
                <Line points={[[-0.3, 0.3, 0], [0.3, -0.3, 0]]} color="#ff5555" lineWidth={2} />
            </group>
        );
    }

    return (
        <group
            ref={refCallback}
            onClick={handleClick}
            position={[pos[0], pos[1], pos[2]]}
            rotation={[0, 0, rotRad]}
            scale={[scale, scale, scale]}
        >
            {children}
        </group>
    );
});

function circlePts(c: number[], r: number): [number, number, number][] {
    const pts: [number, number, number][] = [];
    for (let i = 0; i <= 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        pts.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r, c[2] ?? 0]);
    }
    return pts;
}
function arcPts(c: number[], r: number, a0: number, a1: number): [number, number, number][] {
    let sweep = a1 - a0;
    while (sweep <= 0) sweep += Math.PI * 2;
    const n = Math.max(8, Math.round((sweep / (Math.PI * 2)) * 48));
    const pts: [number, number, number][] = [];
    for (let i = 0; i <= n; i++) {
        const a = a0 + (sweep * i) / n;
        pts.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r, c[2] ?? 0]);
    }
    return pts;
}
